import { Dialog } from "primereact/dialog";
import { InputNumber } from "primereact/inputnumber";
import { RadioButton } from "primereact/radiobutton";
import { Tooltip } from "primereact/tooltip";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import Loading from "../../../../../../components/Loading/Loading";
import { useToastContext } from "../../../../../../contexts/ToastContext/ToastContext";
import useIsMobile from "../../../../../../hooks/useIsMobile";
import localeText from "../../../../../../locale";
import GetInfoService from "../../../../../../service/GetInfoService";
import IospediscoService from "../../../../../../service/iospedisco";
import { PLATFORM_PRODUCT_FEE } from "../../../../../../utilities/constants";
import { formatPrice } from "../../../../../../utilities/formatPrice";
import { localeISO } from "../../../../../../utilities/localeIso";
import { freightInputs, weightInputs } from "../../inputs";
import { radioInputs } from "./radioInputs";
import { Button } from "primereact/button";

const formatValue = (value) => {
  return Number((Math.floor(value * 100) / 100).toFixed(2));
};

const CostCalculator = ({
  isVisible,
  onHide,
  userCurrency,
  selectedCategory,
  selectedSubcategory,
  selectedTypeProduct,
  notHaveFreeSendCloud,
  percentMax,
  percentMin,
  userInfo,
  currencyCode,
  setProductPrice,
  setProductCashbackPercentage,
  setProductWeight,
  setProductHeight,
  setProductWidth,
  setProductLength,
}) => {
  const downloadableId = parseInt(process.env.REACT_APP_DOWNLOADABLE_ID);
  const [radioInput, setRadioInput] = useState(1);
  const [priceInput, setPriceInput] = useState(0);
  const [finalPrice, setFinalPrice] = useState(0);
  const [cashback, setCashback] = useState(percentMin);
  const [weight, setWeight] = useState(0);
  const [freight, setFreight] = useState(false);
  const [freightPrice, setFreightPrice] = useState(null);
  const [isLoadingFreight, setIsLoadingFreight] = useState(false);
  const [isError, setIsError] = useState(false);
  const { isMobile } = useIsMobile();
  const { showErrorToast } = useToastContext();
  const [currencyForex, setCurrencyForex] = useState(1);
  const [height, setHeight] = useState(1);
  const [width, setWidth] = useState(1);
  const [length, setLength] = useState(1);

  const styles = { width: isMobile ? "30rem" : "40rem" };

  const getInfoService = useMemo(() => new GetInfoService(), []);
  const iospediscoService = useMemo(() => new IospediscoService(), []);

  useEffect(() => {
    const getLocationId = async () => {
      try {
        setIsLoadingFreight(true);
        const res = await iospediscoService.getLocatiobId({
          token: localStorage.getItem("__extraconomy_token"),
          postal_code: userInfo?.postalCode,
          country: userInfo?.country,
        });

        const locationId =
          res?.locationResponse?.location_results[0]?.locations_list[0]?.id_location;

        if (locationId) {
          const compare = await iospediscoService.compare({
            token: localStorage.getItem("__extraconomy_token"),
            id_location: locationId,
            width: width,
            height: height,
            weight: weight,
            depth: length,
            pickup_date: userInfo?.amountDaysToPickUp,
          });

          const freight = compare?.comparisonResponse?.comparison_result;
          if (freight) {
            const freightValue = freight[0]?.comparison_items[0]?.total_amount_tax_excluded;
            if (currencyCode !== "EUR") {
              const resForex = await getInfoService.getForexRate("EUR", currencyCode);
              const resFreight = {
                price: freightValue * resForex.result,
                currency: currencyCode,
              };
              setFreightPrice({ price: resFreight, currency: "EUR" });
            } else {
              setFreightPrice({ price: freightValue, currency: "EUR" });
            }
            setIsLoadingFreight(false);
            return;
          }

          if (
            compare?.comparisonResponseError?.comparison_result[0]?.failure_reasons[0]?.includes(
              "minimum volume is 100 cubic centimeters"
            )
          ) {
            showErrorToast({ detail: "minimunValueError" });
            setIsLoadingFreight(false);
          }
        }
      } catch (e) {
        console.log(e);
        setIsLoadingFreight(false);
      }
    };

    if (weight !== 0 && freight === 1) {
      getLocationId();
    }
  }, [
    weight,
    freight,
    showErrorToast,
    iospediscoService,
    userInfo,
    currencyCode,
    getInfoService,
    height,
    width,
    length,
  ]);

  const calcularPrecoCobrado = useCallback((price) => {
    const precoCobrado = price + price * PLATFORM_PRODUCT_FEE;
    setFinalPrice(precoCobrado);
  }, []);

  const calcularValorAReceber = useCallback(
    (price) => {
      const precoFrete = (freightPrice?.price ?? 0) * currencyForex || 0;

      const taxa = price * PLATFORM_PRODUCT_FEE;
      const cashbackValue = price * (cashback / 100);

      const precoSemTaxaDeSeller = price - taxa;

      const precoSemCashback = precoSemTaxaDeSeller - cashbackValue - precoFrete;

      setFinalPrice(precoSemCashback);
    },
    [cashback, freightPrice?.price, currencyForex]
  );

  const applyPriceInProduct = useCallback(
    (priceToProduct) => {
      if (priceInput < 0) {
        showErrorToast({ detail: "valueCanNotBeNegative" });
        return;
      }

      setProductPrice(priceToProduct);
      setProductCashbackPercentage(cashback);
      setProductWeight(weight);
      setProductHeight(height);
      setProductWidth(width);
      setProductLength(length);
      onHide(finalPrice);
    },
    [
      finalPrice,
      priceInput,
      setProductPrice,
      showErrorToast,
      onHide,
      setProductCashbackPercentage,
      cashback,
      setProductWeight,
      weight,
      setProductHeight,
      height,
      setProductWidth,
      width,
      setProductLength,
      length,
    ]
  );

  const taxa = priceInput * PLATFORM_PRODUCT_FEE;

  const cashbackValue = priceInput * (cashback / 100);

  const freightForexValue = (freightPrice?.price ?? 0) * currencyForex;

  const valorFinalRadio1 = (priceInput + freightForexValue) / (0.95 - cashback / 100);

  const cashbackRadio1 = valorFinalRadio1 * (cashback / 100);

  const taxRadio1 = valorFinalRadio1 * 0.05;

  useEffect(() => {
    if (radioInput === 1) calcularPrecoCobrado(priceInput);
    else calcularValorAReceber(priceInput);
  }, [radioInput, calcularPrecoCobrado, calcularValorAReceber, priceInput]);

  useEffect(() => {
    if (freight !== 1) {
      setFreightPrice(null);
      setWeight(0);
    }
  }, [freight, radioInput]);

  useEffect(() => {
    if (finalPrice < 0) setIsError(true);
    else setIsError(false);
  }, [finalPrice]);

  useEffect(() => {
    const getCurrencyForex = async () => {
      if (userCurrency !== "EUR") {
        const resForex = await getInfoService.getForexRate("EUR", userCurrency);
        setCurrencyForex(resForex?.result ? resForex.result : 0);
      }
    };

    getCurrencyForex();
  }, [getInfoService, userCurrency]);

  const hasToCalculateFreight =
    selectedCategory &&
    selectedCategory.id !== downloadableId &&
    selectedSubcategory &&
    selectedTypeProduct !== 2 &&
    !notHaveFreeSendCloud;

  return (
    <Dialog
      header={localeText("priceCalculator")}
      className="m-2"
      style={styles}
      visible={isVisible}
      onHide={onHide}
      draggable={false}
    >
      <div>
        <div className="flex align-items-center gap-3 mt-1">
          {radioInputs.map((input) => (
            <div key={input.value} className="flex align-items-center">
              <RadioButton
                inputId={input.type}
                name={input.name}
                className="cursor-pointer"
                value={input.value}
                disabled={isLoadingFreight}
                onChange={(e) => setRadioInput(e.value)}
                checked={radioInput === input.value}
              />
              <label htmlFor={input.type} className="ml-2 cursor-pointer">
                {localeText(input.label)}
              </label>
            </div>
          ))}
        </div>

        <div className="mt-4">
          <div>
            {radioInput === 1 ? (
              <strong className="mb-1 text-lg">
                {localeText("ifYouWishToReceive")}: {userCurrency} {formatPrice(priceInput)}
              </strong>
            ) : (
              <strong className="mb-1 text-lg">
                {localeText("theCustomerWillPay")}: {userCurrency} {formatPrice(priceInput + taxa)}
              </strong>
            )}
          </div>
          {freight === 1 && isLoadingFreight && (
            <div className="flex flex-row gap-2 align-items-center">
              <Loading classNameContainer={"my-0 text-left"} disableMargin />
              <p>{localeText("lookingTheBestShippingMethod")}</p>
            </div>
          )}

          {radioInput === 1 && (
            <div className="flex flex-column gap-1">
              <strong>{localeText("additionals")}:</strong>
              <p className="mb-0">
                + Cashback: {userCurrency} {formatPrice(cashbackRadio1)}
              </p>
              <p className="mb-0">
                + {localeText("tax")} ({localeText("seller")}): {userCurrency}{" "}
                {formatPrice(taxRadio1)}
              </p>
              <p className="mb-0">
                + {localeText("tax")} ({localeText("byBuyer")}): {userCurrency}{" "}
                {formatPrice(taxRadio1)}
              </p>
              {freightPrice && (
                <p className="mb-0">
                  + {localeText("freight")}: {userCurrency} {formatPrice(freightForexValue)}
                </p>
              )}
              <hr className="my-1" />

              <div className="flex justify-content-between align-items-center">
                <div>
                  <p className="mb-0">
                    <strong>
                      {localeText("thenYouWillHaveToCharge")}: {userCurrency}{" "}
                      {formatPrice(formatValue(valorFinalRadio1))}
                    </strong>
                  </p>
                  <p className="mb-0">
                    <strong>
                      {localeText("finalPriceForClient")}: {userCurrency}{" "}
                      {formatPrice(formatValue(valorFinalRadio1) + formatValue(taxRadio1))}
                    </strong>
                  </p>
                </div>

                <Button
                  label={localeText("apply")}
                  onClick={() => applyPriceInProduct(valorFinalRadio1)}
                />
              </div>
            </div>
          )}

          {radioInput === 2 && (
            <div className="flex flex-column gap-1">
              <strong>{localeText("discounts")}:</strong>
              <p className="mb-0">
                - Cashback: {userCurrency} {formatPrice(cashbackValue)}
              </p>
              <p className="mb-0">
                - {localeText("tax")} ({localeText("seller")})(5%): {userCurrency}{" "}
                {formatPrice(taxa)}
              </p>
              <p className="mb-0">
                - {localeText("tax")} ({localeText("byBuyer")})(5%): {userCurrency}{" "}
                {formatPrice(taxa)}
              </p>
              {freightPrice && (
                <p className="mb-0">
                  - {localeText("freight")}: {userCurrency} {formatPrice(freightForexValue)}
                </p>
              )}

              <hr className="my-1" />

              <div className="flex justify-content-between align-items-center">
                <p className="m-0">
                  <strong>
                    {localeText("amountToBeReceived")}: {userCurrency} {formatPrice(finalPrice)}
                  </strong>
                </p>

                <Button
                  label={localeText("apply")}
                  onClick={() => applyPriceInProduct(priceInput)}
                />
              </div>
            </div>
          )}

          {isError && <p className="text-red-400 text-sm">{localeText("valueCanNotBeNegative")}</p>}
          <div className="flex flex-column gap-2 mt-3">
            <label htmlFor="price" className="text-sm">
              {localeText("price")} {localeText("or")} {localeText("promotionalPrice")}
            </label>
            <InputNumber
              mode="currency"
              currency={userCurrency}
              locale={localeISO}
              className="w-full"
              name="priceUniqueModal"
              min={0}
              max={10000000}
              value={priceInput}
              disabled={isLoadingFreight}
              onChange={(e) => setPriceInput(e.value)}
            />

            <label htmlFor="price" className="text-sm">
              Cashback (%)
            </label>
            <InputNumber
              className="w-full"
              name="cashbackModal"
              min={percentMin}
              max={percentMax}
              value={cashback}
              disabled={isLoadingFreight}
              onChange={(e) => setCashback(e.value)}
            />

            {hasToCalculateFreight && (
              <>
                <ul className="list-none mx-0 mt-2 p-0">
                  <li>
                    <label htmlFor="height">{localeText("height")} (cm)</label>
                  </li>
                  <li>
                    <InputNumber
                      name="height"
                      className="my-2 w-full"
                      value={height}
                      min={1}
                      locale={localeISO}
                      disabled={isLoadingFreight}
                      onValueChange={(e) => {
                        if (e.value <= 0) {
                          showErrorToast({ detail: "dimensionsGreaterThanZero" });
                        } else setHeight(e.value);
                      }}
                      mode="decimal"
                      minFractionDigits={2}
                    />
                  </li>
                  <li>
                    <label htmlFor="width">{localeText("width")} (cm)</label>
                  </li>
                  <li>
                    <InputNumber
                      name="width"
                      className="my-2 w-full"
                      value={width}
                      min={1}
                      locale={localeISO}
                      disabled={isLoadingFreight}
                      onValueChange={(e) => {
                        if (e.value <= 0) {
                          showErrorToast({ detail: "dimensionsGreaterThanZero" });
                        } else setWidth(e.value);
                      }}
                      mode="decimal"
                      minFractionDigits={2}
                    />
                  </li>
                  <li>
                    <label htmlFor="length">{localeText("length")} (cm)</label>
                  </li>
                  <li>
                    <InputNumber
                      name="length"
                      className="my-2 w-full"
                      value={length}
                      min={1}
                      locale={localeISO}
                      disabled={isLoadingFreight}
                      onValueChange={(e) => {
                        if (e.value <= 0) {
                          showErrorToast({ detail: "dimensionsGreaterThanZero" });
                        } else setLength(e.value);
                      }}
                      mode="decimal"
                      minFractionDigits={2}
                    />
                  </li>
                  <li className="mb-2">
                    <label htmlFor="freight" className="mt-2">
                      {localeText("freight")}
                    </label>
                    <div className="mt-2 flex md:flex-row flex-column flex-wrap gap-3">
                      {freightInputs.map((freightInput, i) => {
                        if (notHaveFreeSendCloud && freightInput.label === "free") return null;
                        return (
                          <div key={i} className="flex align-items-center">
                            <RadioButton
                              inputId={`freightModal${i}`}
                              name={freightInput.name}
                              value={freightInput.value}
                              onChange={(e) => setFreight(e.value)}
                              disabled={isLoadingFreight}
                              checked={freight === freightInput.value}
                            />
                            <label htmlFor={`freightModal${i}`} className="ml-2 cursor-pointer">
                              {localeText(freightInput.label)}{" "}
                              {freightInput.label === "free" && "(Sendcloud)"}
                              {freightInput.label === "free2" &&
                                `(${localeText("without")} Sendcloud)`}
                            </label>
                            <Tooltip target=".extracoin-tooltip" />
                            <i
                              className="pi pi-info-circle ml-1 extracoin-tooltip cursor-pointer text-600"
                              data-pr-tooltip={`${localeText(freightInput.explain)}`}
                              data-pr-position="right"
                              data-pr-at="right+5 top"
                              data-pr-my="left center-2"
                              tooltipoptions={{ event: "focus" }}
                            ></i>
                          </div>
                        );
                      })}
                    </div>
                  </li>
                  {freight === 1 && (
                    <>
                      <li>
                        <label htmlFor="weight" className="flex align-items-center flex-wrap gap-2">
                          {localeText("chooseWeightRange")} (kg){" "}
                          {isLoadingFreight && (
                            <Loading classNameContainer={"my-0 text-left"} disableMargin />
                          )}
                        </label>
                        {freight === 1 && weight === 0 && (
                          <p className="text-red-400 text-sm">
                            {localeText("chooseWeightRangeToCalculateShipping")}
                          </p>
                        )}
                      </li>
                      <li className="mt-2 flex md:flex-row flex-column flex-wrap gap-3">
                        {weightInputs.map((weightInput, i) => (
                          <div key={i} className="flex align-items-center">
                            <RadioButton
                              inputId={`weightModal${i}`}
                              name={weightInput.name}
                              value={weightInput.value}
                              disabled={isLoadingFreight}
                              onChange={(e) => setWeight(e.value)}
                              checked={weight === weightInput.value}
                            />
                            <label htmlFor={`weightModal${i}`} className="ml-2 cursor-pointer">
                              {weightInput.label}
                            </label>
                          </div>
                        ))}
                      </li>
                    </>
                  )}
                </ul>
              </>
            )}
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default CostCalculator;
